/**
 * PowerSync sync-fetch diagnostics.
 *
 * When PowerSync's HTTP calls (notably `getWriteCheckpoint` → `updateLocalTarget`)
 * fail, the browser only hands JS an opaque `TypeError: Failed to fetch` — the
 * real reason (CORS header rejection, DNS/cert/connection failure, or a
 * blocked-by-client extension) is printed by the browser as a separate console
 * line and is NOT observable from script. That makes a stuck-checkpoint loop
 * ("Last synced: Never", the "Sync needs a refresh" modal) impossible to
 * diagnose from logs alone.
 *
 * This probes the endpoint two ways to *infer* the cause:
 *
 *   no-cors resolves + cors throws  → reachable, blocked by CORS (header config)
 *   no-cors throws  + cors throws   → unreachable (DNS/cert/connection/extension)
 *   no-cors resolves + cors resolves→ endpoint + CORS OK (look at auth / 5xx)
 *
 * `no-cors` yields an *opaque* response whenever the server responds at all
 * (even 401/404), so it's a pure reachability signal; `cors` mode fails
 * specifically when the `Access-Control-Allow-Origin` headers are wrong.
 */

export interface SyncFetchDiagnosis {
  endpoint: string;
  /** The `/write-checkpoint2.json` URL actually probed. */
  probedUrl: string;
  /** True when the server responded at all (opaque no-cors response resolved). */
  reachable: boolean;
  /** True when the endpoint is reachable but the CORS-mode request was rejected. */
  corsBlocked: boolean;
  /** navigator.onLine at probe time. */
  online: boolean;
  /** One-line, human-readable verdict. */
  verdict: string;
}

/** Latest diagnosis, exposed so the app can surface it in the UI. */
let latestDiagnosis: SyncFetchDiagnosis | null = null;
const listeners = new Set<(d: SyncFetchDiagnosis) => void>();

export function getLatestSyncFetchDiagnosis(): SyncFetchDiagnosis | null {
  return latestDiagnosis;
}

/** Subscribe to new diagnoses. Returns an unsubscribe fn. */
export function onSyncFetchDiagnosis(fn: (d: SyncFetchDiagnosis) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

async function probe(url: string, mode: RequestMode): Promise<boolean> {
  try {
    await fetch(url, { mode, cache: 'no-store', credentials: 'omit' });
    return true;
  } catch {
    return false;
  }
}

/**
 * Probe the PowerSync endpoint to infer why an HTTP `fetch` is failing.
 * Never throws; logs a `[PowerSync][diag]` verdict and returns the structured
 * result (also cached for `getLatestSyncFetchDiagnosis`).
 */
export async function diagnoseSyncFetchFailure(endpoint: string): Promise<SyncFetchDiagnosis> {
  const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
  let probedUrl = endpoint;
  try {
    probedUrl = new URL('/write-checkpoint2.json', endpoint).toString();
  } catch {
    // endpoint isn't a valid absolute URL — keep it as-is for the log.
  }

  // no-cors first (reachability), then cors (header check).
  const reachable = await probe(probedUrl, 'no-cors');
  const corsOk = await probe(probedUrl, 'cors');
  const corsBlocked = reachable && !corsOk;

  let verdict: string;
  if (!online) {
    verdict = 'Device is offline (navigator.onLine=false).';
  } else if (reachable && corsBlocked) {
    verdict =
      'Sync endpoint is reachable but the request was blocked by CORS — the ' +
      'HTTP route is missing or returning a bad/duplicate Access-Control-Allow-Origin header.';
  } else if (!reachable) {
    verdict =
      'Sync endpoint is unreachable over HTTP — DNS/cert/connection failure, or ' +
      'blocked by a browser extension / ad-blocker / VPN on this device.';
  } else {
    // The probe is unauthenticated, so a protected endpoint returns 401 — which,
    // now that it carries valid CORS headers, resolves cleanly. This branch only
    // proves reachability + CORS are fine; it can't test the authenticated
    // request. If sync is still stuck, inspect the real (token-bearing)
    // write-checkpoint request in the Network tab (200 vs 401 vs 5xx).
    verdict =
      'Sync endpoint is reachable and CORS headers are valid. If sync is still ' +
      'stuck, check the authenticated checkpoint request (Network tab) for a ' +
      'non-2xx status — this probe cannot test it.';
  }

  const diagnosis: SyncFetchDiagnosis = {
    endpoint,
    probedUrl,
    reachable,
    corsBlocked,
    online,
    verdict,
  };

  console.warn('[PowerSync][diag] ' + verdict, {
    endpoint,
    probedUrl,
    reachable,
    corsBlocked,
    online,
  });

  latestDiagnosis = diagnosis;
  for (const fn of listeners) {
    try {
      fn(diagnosis);
    } catch {
      // never let a listener break diagnostics
    }
  }
  return diagnosis;
}
